import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import Category from "@mui/icons-material/Category";
import Person from "@mui/icons-material/Person";
import CalendarToday from "@mui/icons-material/CalendarToday";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";

// --- กลุ่มรายงานรับซื้อ ---
const purchaseReports = [
  {
    label: "สรุปยอดซื้อประจำวัน",
    icon: <CalendarToday style={{ fontSize: 32 }} />,
    route: "/reports/daily_purchases",
    permission: ["report_purchase_items"],
    gradient: "from-blue-500 to-indigo-600",
    shadow: "shadow-blue-200",
    hoverShadow: "hover:shadow-blue-300/50",
  },
  {
    label: "สรุปยอดซื้อสินค้ารายบุคคล",
    icon: <Person style={{ fontSize: 32 }} />,
    route: "/reports/individual_purchases",
    permission: ["report_purchase_items"],
    gradient: "from-blue-500 to-indigo-600",
    shadow: "shadow-blue-200",
    hoverShadow: "hover:shadow-blue-300/50",
  },
  {
    label: "สรุปยอดซื้อสินค้ารายชนิด",
    icon: <Category style={{ fontSize: 32 }} />,
    route: "/reports/summary_purchases_product",
    permission: ["report_purchase_items"],
    gradient: "from-blue-500 to-indigo-600",
    shadow: "shadow-blue-200",
    hoverShadow: "hover:shadow-blue-300/50",
  },
  {
    label: "สรุปยอดซื้อรายตั๋ว",
    icon: <ReceiptLong style={{ fontSize: 32 }} />,
    route: "/reports/summary_ticket_purchases",
    permission: ["report_purchase_items"],
    gradient: "from-blue-500 to-indigo-600",
    shadow: "shadow-blue-200",
    hoverShadow: "hover:shadow-blue-300/50",
  },
  {
    label: "ต้นทุนสินค้าคงเหลือ",
    icon: <MonetizationOnIcon style={{ fontSize: 32 }} />,
    route: "/reports/cost_inventory",
    permission: ["report_purchase_items"],
    gradient: "from-blue-500 to-indigo-600",
    shadow: "shadow-blue-200",
    hoverShadow: "hover:shadow-blue-300/50",
  },
  {
    label: "10 อันดับ ยอดซื้อรายบุคคลประจำเดือน",
    icon: <ReceiptLong style={{ fontSize: 32 }} />,
    route: "/reports/top_monthly_purchases",
    permission: ["report_purchase_items"],
    gradient: "from-blue-500 to-indigo-600",
    shadow: "shadow-blue-200",
    hoverShadow: "hover:shadow-blue-300/50",
  },
];

// --- กลุ่มรายงานการขาย ---
const salesReports = [
  {
    label: "สรุปยอดขายประจำวัน",
    icon: <CalendarToday style={{ fontSize: 32 }} />,
    route: "/reports/daily_sales",
    permission: ["report_sale_items"],
    gradient: "from-orange-400 to-red-500",
    shadow: "shadow-orange-200",
    hoverShadow: "hover:shadow-orange-300/50",
  },
  {
    label: "สรุปยอดขายสินค้ารายบุคคล",
    icon: <Person style={{ fontSize: 32 }} />,
    route: "/reports/individual_sales",
    permission: ["report_sale_items"],
    gradient: "from-orange-400 to-red-500",
    shadow: "shadow-orange-200",
    hoverShadow: "hover:shadow-orange-300/50",
  },
  {
    label: "สรุปยอดขายสินค้ารายชนิด",
    icon: <Category style={{ fontSize: 32 }} />,
    route: "/reports/summary_sales_product",
    permission: ["report_sale_items"],
    gradient: "from-orange-400 to-red-500",
    shadow: "shadow-orange-200",
    hoverShadow: "hover:shadow-orange-300/50",
  },
  {
    label: "สรุปยอดขายรายตั๋ว",
    icon: <ReceiptLong style={{ fontSize: 32 }} />,
    route: "/reports/summary_ticket_sales",
    permission: ["report_sale_items"],
    gradient: "from-orange-400 to-red-500",
    shadow: "shadow-orange-200",
    hoverShadow: "hover:shadow-orange-300/50",
  },
  {
    label: "10 อันดับ ยอดขายรายบุคคลประจำเดือน",
    icon: <ReceiptLong style={{ fontSize: 32 }} />,
    route: "/reports/top_monthly_sales",
    permission: ["report_sale_items"],
    gradient: "from-orange-400 to-red-500",
    shadow: "shadow-orange-200",
    hoverShadow: "hover:shadow-orange-300/50",
  },
];

const purchasePrice = [
  {
    label: "ราคารับซื้อ",
    icon: <ReceiptLong style={{ fontSize: 32 }} />,
    route: "/reports/purchase_price",
    permission: [], // ว่าง = ใครก็เห็น (หรือจะใส่ key ทีหลังได้)
    gradient: "from-purple-500 to-pink-500",
    shadow: "shadow-purple-200",
    hoverShadow: "hover:shadow-purple-300/50",
  },
];

const ReportsPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (auth !== null) setIsLoading(false);
  }, [auth]);

  const hasPerm = (required = []) => {
    if (!required || required.length === 0) return true;
    const userPerms = auth?.permissions || {};
    return required.some((k) => userPerms[k] === true);
  };

  const filterReports = (list) => list.filter((r) => hasPerm(r.permission));

  const filteredPurchaseReports = useMemo(
    () => filterReports(purchaseReports),
    [auth]
  );
  const filteredSalesReports = useMemo(
    () => filterReports(salesReports),
    [auth]
  );
  const filteredPurchasePrice = useMemo(
    () => filterReports(purchasePrice),
    [auth]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderReportGroup = (title, reports) => {
    if (!reports || reports.length === 0) return null;
    return (
      <div className="w-full mb-12 animate-fade-in-up">
        <h2 className="text-xl font-bold text-gray-800 mb-6 text-center md:text-left flex items-center justify-center md:justify-start gap-2">
          <span className="w-1 h-8 bg-gradient-to-b from-green-400 to-green-600 rounded-full hidden md:block"></span>
          {title}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 w-full">
          {reports.map((btn, index) => (
            <div
              key={`${btn.route}-${index}`}
              onClick={() =>
                btn.external
                  ? window.open(btn.route, "_blank")
                  : navigate(btn.route)
              }
              className={`
                bg-white rounded-2xl p-6 
                flex flex-col items-center justify-center 
                cursor-pointer transition-all duration-300 
                border border-transparent hover:border-gray-100
                shadow-sm ${btn.hoverShadow} hover:-translate-y-2
                group relative overflow-hidden h-full
              `}
              style={{
                animationDelay: `${index * 50}ms`, // Stagger effect
                animationFillMode: 'both'
              }}
            >
              {/* Decorative background blob */}
              <div className="absolute -right-4 -top-4 w-20 h-20 bg-gray-50 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500 ease-out z-0"></div>

              <div
                className={`
                    w-16 h-16 rounded-2xl bg-gradient-to-br ${btn.gradient} 
                    text-white flex items-center justify-center mb-4 
                    transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 
                    shadow-md z-10
                `}
              >
                {btn.icon}
              </div>
              <span className="text-gray-700 font-medium text-center whitespace-pre-line group-hover:text-gray-900 transition-colors text-sm md:text-base z-10">
                {btn.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const showPurchaseGroup = filteredPurchaseReports.length > 0;
  const showSalesGroup = filteredSalesReports.length > 0;

  return (
    <div className="h-screen overflow-y-auto bg-gray-50/50 p-4">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate3d(0, 20px, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>
      <div className="max-w-7xl mx-auto pb-10">
        <div className="mb-8">
          <AppBreadcrumbs />
        </div>
        
        <div className="flex flex-col items-center w-full">
            <div className="text-center mb-10 animate-fade-in-up">
                <h1 className="text-2xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-700 to-gray-900 mb-2">
                    รายงาน
                </h1>
                <p className="text-gray-500 text-sm md:text-base">
                    เลือกดูรายงานสรุปยอดซื้อและขายของคุณได้ที่นี่
                </p>
            </div>

          {showPurchaseGroup &&
            renderReportGroup("รายงานรับซื้อ", filteredPurchaseReports)}
          
          {showSalesGroup &&
            renderReportGroup("รายงานการขาย", filteredSalesReports)}
            
          {renderReportGroup("ราคารับซื้อ", filteredPurchasePrice)}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
